
import { ExternalLink, Star } from "lucide-react";
import { CardContent, CardFooter } from "@/components/ui/card";
import { ActivityContentProps } from "./types";

const ActivityContent = ({
  title,
  description,
  rating,
  numberOfRatings,
  price,
  currency,
  type,
  bookingUrl,
  onBookingClick
}: ActivityContentProps) => {
  const truncateDescription = (text: string) => {
    return text.length > 200 ? text.substring(0, 200) + "..." : text;
  };

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const getPriceDisplayTitle = (price: string) => {
    if (type === "food") {
      let priceLevel = price // 1-4
      return (
        <span className="text-sm text-muted-foreground">
          {priceLevel === "1" && "$"}
          {priceLevel === "2" && "$$"}
          {priceLevel === "3" && "$$$"}
          {priceLevel === "4" && "$$$$"}
          &nbsp;
        </span>
      )
    }
    else {
      return "";
    }
  }

  const getPriceDisplay = (price: string) => {
    if (!price || price === "0" || price === "undefined" || price === "0 undefined") {
      return "Check prices"
    }

    return price + " " + currency;
  };

  return (
    <div className="md:w-2/3">
      <CardContent 
        className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
        onClick={onBookingClick}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-base">{getPriceDisplayTitle(price)}{title}</h3>
          {rating && (
            <div className="flex items-center gap-1 text-sm">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{rating.toFixed(1)}</span>
              {numberOfRatings && (
                <span className="text-muted-foreground">({numberOfRatings})</span>
              )}
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {truncateDescription(stripHtml(description))}
        </p>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        {type !== "food" && (
            <button
              onClick={onBookingClick}
              className="flex items-center gap-2 text-primary hover:underline"
              aria-label={`View booking options for ${title}`}
            >
              {getPriceDisplay(price)} <ExternalLink className="h-4 w-4" />
            </button>
        )}

        {type == "food" && (
          <svg style={{display:"flex", flex:"auto"}} width="100" height="30" viewBox="0 0 133 40" xmlns="http://www.w3.org/2000/svg" className="css-4zleql e1i2rwtb0"><g className="css-7tutpn e1s4fskc1"><path d="M0.702148 13.6424H6.79825V28.6958H10.2551V13.6424H16.3745V11.0498H0.702148V13.6424Z" fill="hsl(174, 100%, 20%)"></path><path d="M21.2972 11.0588H17.9338V28.6958H21.2972V22.2494C21.2972 19.0729 23.4694 17.1342 25.4313 17.1342C26.7627 17.1342 27.7436 17.9984 27.7436 19.7502V28.6958H31.107V18.6758C31.107 15.8263 29.2151 14.4482 26.9729 14.4482C24.6605 14.4482 22.2314 15.9898 21.2972 18.8393V11.0588Z" fill="hsl(174, 100%, 20%)"></path><path fillRule="evenodd" clipRule="evenodd" d="M32.7483 21.5953C32.7483 18.0217 35.0373 14.4482 39.6619 14.4482C43.399 14.4482 45.3843 16.7138 45.3843 20.9647V21.6187H35.5745C35.9482 23.8376 37.6532 25.5893 40.5261 25.5893C42.2545 25.5893 44.0997 24.9587 45.6179 23.7441V27.0374C43.8895 28.3221 41.9275 28.9527 39.9889 28.9527C35.4577 28.9527 32.7483 25.6127 32.7483 21.5953ZM41.9742 19.9837C41.9742 17.6247 41.0166 16.4568 39.2181 16.4568C37.0693 16.4568 35.808 18.0918 35.5511 19.9837H41.9742Z" fill="hsl(174, 100%, 20%)"></path><path d="M59.8752 11.0498H47.3326L47.3326 28.6958H50.8128V20.6027H58.7073V18.0101H50.8128V13.6424H59.8752V11.0498Z" fill="hsl(174, 100%, 20%)"></path><path fillRule="evenodd" clipRule="evenodd" d="M60.1898 21.7355C60.1898 18.1152 62.7825 14.4482 67.7808 14.4482C72.6856 14.4482 75.2315 18.0451 75.2315 21.6654C75.2315 25.309 72.639 28.9527 67.6406 28.9527C62.7591 28.9527 60.1898 25.3324 60.1898 21.7355ZM67.8742 26.3601C70.4434 26.3601 71.7981 24.3047 71.7981 21.8756C71.7981 19.3297 70.3267 17.0408 67.5472 17.0408C64.9779 17.0408 63.6232 19.0961 63.6232 21.5019C63.6232 24.0711 65.0947 26.3601 67.8742 26.3601Z" fill="hsl(174, 100%, 20%)"></path><path d="M79.9794 14.6818H76.8729L76.8729 28.6959H80.2363V20.2407C80.937 18.5123 82.5953 17.5781 84.3003 17.5781C85.2113 17.5781 86.1689 17.835 86.893 18.3488V15.0555C86.3324 14.7052 85.6083 14.495 84.8843 14.495C82.7822 14.495 80.7268 15.8263 79.9794 19.0028V14.6818Z" fill="hsl(174, 100%, 20%)"></path><path d="M94.9308 20.3396L101.6 13.2385H97.7859L91.9711 19.2355L91.9694 11.0498H88.6077V28.6958H91.9938L91.9694 21.4993C91.9694 21.4993 97.543 21.3624 97.543 28.6958H101.188C101.188 21.5954 94.9308 20.3396 94.9308 20.3396Z" fill="hsl(174, 100%, 20%)"></path></g><path d="M104.516 20.1212C104.523 13.5916 109.321 7.02094 118.766 7.02094C127.965 7.02094 132.754 13.6649 132.646 19.957C132.521 27.2509 127.659 31.024 123.304 32.3805L122.807 30.2057C122.548 29.0656 122.856 28.0485 123.663 27.3296C124.705 26.4 125.326 25.2505 125.649 23.6611C125.99 21.9807 125.83 20.1365 125.072 18.2945L122.496 11.0501H119.912L121.977 19.3256C122.166 20.1499 121.54 20.936 120.694 20.936L118.185 9.93267H115.837L118.096 23.5118C116.681 23.6388 116.157 22.7211 116.04 22.057L114.599 11.8014H112.213C112.213 11.8014 112.228 17.3264 112.228 21.0997C112.228 26.6456 115.192 28.2965 117.239 28.789C118.397 29.0675 119.171 29.968 119.381 31.2323L119.672 32.979C115.287 32.9903 104.504 31.3584 104.516 20.1212Z" fill="hsl(174, 100%, 20%)" className="css-7tutpn e1s4fskc0"></path></svg>
        )}
      </CardFooter>
    </div>
  );
};

export default ActivityContent;
